// Consent-based first-party analytics (+ optional GA4). No personal data is ever sent.
const CONSENT_KEY = 'cookie_consent';
const VISITOR_KEY = 'it_vid';
const SESSION_KEY = 'it_sid';
const LAST_SEEN_KEY = 'it_last';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const ENDPOINT = '/api/analytics/collect';

let queue = [];
let flushTimer = null;
let sessionMeta = null;
let gaLoaded = false;
let isNewVisitor = false;

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

export const getConsent = () => localStorage.getItem(CONSENT_KEY);

const isExcluded = () => !!localStorage.getItem('adminToken') || window.location.pathname.startsWith('/admin') || navigator.webdriver;

export const canTrack = () => getConsent() === 'granted' && !isExcluded();

const deviceType = () => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return 'tablet';
  if (/Mobi|iPhone|Android/i.test(ua)) return 'mobile';
  return 'desktop';
};

const ensureIds = () => {
  let vid = localStorage.getItem(VISITOR_KEY);
  if (!vid) {
    vid = uid();
    localStorage.setItem(VISITOR_KEY, vid);
    isNewVisitor = true;
  }
  const now = Date.now();
  const last = Number(localStorage.getItem(LAST_SEEN_KEY) || 0);
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid || now - last > SESSION_TIMEOUT_MS) {
    sid = uid();
    sessionStorage.setItem(SESSION_KEY, sid);
    const params = new URLSearchParams(window.location.search);
    sessionMeta = {
      landing_path: window.location.pathname,
      referrer: document.referrer || null,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      gclid: params.has('gclid'),
      device: deviceType(),
    };
    sessionStorage.setItem('it_smeta', JSON.stringify(sessionMeta));
  } else {
    sessionMeta = sessionMeta || JSON.parse(sessionStorage.getItem('it_smeta') || 'null') || { landing_path: window.location.pathname, device: deviceType() };
  }
  localStorage.setItem(LAST_SEEN_KEY, String(now));
  return { vid, sid };
};

const flush = (useBeacon = false) => {
  if (!queue.length || !canTrack()) return;
  const { vid, sid } = ensureIds();
  const body = JSON.stringify({ visitor_id: vid, session_id: sid, is_new_visitor: isNewVisitor, session: sessionMeta, events: queue.splice(0, 50) });
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
  } else {
    fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }
};

const gtagEvent = (name, params) => {
  if (gaLoaded && window.gtag) window.gtag('event', name, params);
};

export const trackEvent = (type, data = {}) => {
  if (!canTrack()) return;
  ensureIds();
  queue.push({ event_id: uid(), type, path: window.location.pathname, ...data });
  clearTimeout(flushTimer);
  flushTimer = setTimeout(() => flush(), 800);
  if (type !== 'page_view' && type !== 'page_engagement') gtagEvent(type, { tour_slug: data.tour_slug });
};

export const getAnalyticsIds = () => {
  if (!canTrack()) return {};
  const { vid, sid } = ensureIds();
  return { analytics_visitor_id: vid, analytics_session_id: sid };
};

export const loadGA4 = (measurementId) => {
  if (gaLoaded || !measurementId || !canTrack()) return;
  gaLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { anonymize_ip: true, send_page_view: false });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);
};

let pageStart = null;
let pagePath = null;
let pageSlug = null;

export const trackPageView = (title, tourSlug) => {
  endPage();
  pageStart = Date.now();
  pagePath = window.location.pathname;
  pageSlug = tourSlug || null;
  trackEvent('page_view', { title: (title || document.title || '').slice(0, 200), tour_slug: pageSlug });
  gtagEvent('page_view', { page_path: pagePath, page_title: title || document.title });
};

export const endPage = (useBeacon = false) => {
  if (!pageStart || !canTrack()) return;
  const ms = Math.min(Date.now() - pageStart, 3600000);
  queue.push({ event_id: uid(), type: 'page_engagement', path: pagePath, tour_slug: pageSlug, engagement_ms: ms });
  pageStart = null;
  flush(useBeacon);
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => endPage(true));
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.dataset.track) return;
    const href = a.getAttribute('href') || '';
    const slug = window.location.pathname.startsWith('/turlar/') ? window.location.pathname.split('/')[2] : null;
    if (/wa\.me|whatsapp\.com/i.test(href)) trackEvent('whatsapp_click', { tour_slug: slug });
    else if (href.startsWith('tel:')) trackEvent('phone_click', { tour_slug: slug });
  }, true);
}

export const setConsent = (value) => {
  localStorage.setItem(CONSENT_KEY, value);
  if (value !== 'granted') {
    localStorage.removeItem(VISITOR_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    queue = [];
  }
};
